import { Link } from 'react-router-dom';

import { useAgents } from '@/hooks/useAgents';
import { useSessions } from '@/hooks/useSessions';
import { useAuth } from '@/auth/AuthContext';
import { SessionBadge } from '@/components/common/StatusBadge';
import { Loading, ErrorBanner, EmptyState } from '@/components/common/States';
import { isAgentOnline, relTime, fmtDuration, shortId } from '@/lib/format';

function Kpi({ label, value, unit, lit }: { label: string; value: string; unit?: string; lit?: boolean }) {
  return (
    <div className="card kpi">
      <div className="k-label">{label}</div>
      <div className={`k-value${lit ? ' lit' : ''}`}>
        {value}
        {unit && <span className="k-unit">{unit}</span>}
      </div>
    </div>
  );
}

export function Overview() {
  const { user } = useAuth();
  const agents = useAgents();
  const sessions = useSessions();

  const online = agents.data?.filter((a) => isAgentOnline(a.last_seen_at)).length ?? 0;
  const active = sessions.data?.filter((s) => s.status === 'active').length ?? 0;
  const recent = (sessions.data ?? []).slice(0, 6);

  return (
    <div className="fade-up">
      <div className="page-head spread">
        <div>
          <div className="eyebrow">console</div>
          <div className="title">Overview</div>
          <div className="sub">
            Signed in as <span className="mono">{user?.email}</span>
          </div>
        </div>
        <Link to="/sessions/new" className="btn btn-primary">+ New test</Link>
      </div>

      <div className="kpi-grid" style={{ marginBottom: 24 }}>
        <Kpi label="Agents online" value={String(online)} unit={`/ ${agents.data?.length ?? 0}`} lit={online > 0} />
        <Kpi label="Active tests" value={String(active)} lit={active > 0} />
        <Kpi label="Sessions total" value={String(sessions.data?.length ?? 0)} />
        <Kpi label="Traffic balance" value={fmtDuration(user?.traffic_seconds_balance)} />
      </div>

      <div className="spread" style={{ marginBottom: 12 }}>
        <div className="eyebrow">recent sessions</div>
        <Link to="/sessions" className="mono" style={{ fontSize: 12 }}>view all →</Link>
      </div>

      {sessions.isLoading ? (
        <Loading label="Loading sessions" />
      ) : sessions.error ? (
        <ErrorBanner error={sessions.error} />
      ) : recent.length === 0 ? (
        <EmptyState
          title="No tests yet"
          hint="Launch your first traffic test to see it here."
          action={<Link to="/sessions/new" className="btn btn-primary">+ New test</Link>}
        />
      ) : (
        <div className="panel">
          <table className="table">
            <thead>
              <tr>
                <th>Session</th>
                <th>Mode</th>
                <th>Status</th>
                <th>Duration</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link to={`/sessions/${s.id}`} className="mono">
                      {s.label || shortId(s.id)}
                    </Link>
                  </td>
                  <td className="mono dim">{s.test_mode}</td>
                  <td><SessionBadge status={s.status} /></td>
                  <td className="mono">{fmtDuration(s.duration_seconds)}</td>
                  <td className="dim">{relTime(s.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
